import "app/landing/landing.scss";

import {Offcanvas} from "bootstrap";
import image1 from "./assets/image-1.png";
import image2 from "./assets/image-2.png";
import image3 from "./assets/image-3.png";
import image4 from "./assets/image-4.png";
import image5 from "./assets/image-5.png";
import image6 from "./assets/image-6.jpg";
import cpd from "./assets/cpd.jpg";
import naKafeVideo from "./assets/na-kafe-video.jpg";
import {type RefCallback, useCallback, useMemo, useRef} from "react";
import {useUnmount} from "@fluentui/react-hooks";
import {styled} from "styled-components";
import LogoSvg from "app/utils/icons/logo-only.svg?react";
import {Trans, useTranslation} from "react-i18next";
import {supportedLanguages} from "app/employee/i18n";
import FlagBgSvg from "./flags/bg.svg?react";
import FlagGbSvg from "./flags/gb.svg?react";
import FlagRuSvg from "./flags/ru.svg?react";
import FlagZhSvg from "./flags/zh.svg?react";
import LanguageSvg from "bootstrap-icons/icons/translate.svg?react";
import GooglePlayBgSvg from "./assets/google_play_badge_bg.svg?react";
import GooglePlayEnSvg from "./assets/google_play_badge_en.svg?react";
import {BootstrapDropdown} from "app/landing/BootstrapDropdown";
import {OpenInSafariBadge} from "app/landing/OpenInSafariBadge";
import ResponsiveImage from "app/landing/ResponsiveImage";
import {QRCode} from "react-qr-code";
import {YoutubeLink} from "app/landing/YoutubeLink";

type DeviceType = "android" | "ios" | "other";

const detectDevice = (): DeviceType => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  if (/ipad|iphone|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  return "other";
};

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=bg.fsacademy";
const NA_KAFE_VIDEO_ID = "dQ9PSKjrCBs";

const Logo = styled(LogoSvg)`
  height: 2rem;
`;

const svgFlags = {
  "bg-BG": <FlagBgSvg />,
  "en-GB": <FlagGbSvg />,
  "ru-RU": <FlagRuSvg />,
  "zh-CN": <FlagZhSvg />
} as const;

const playBadges = {
  "bg-BG": GooglePlayBgSvg,
  "en-GB": GooglePlayEnSvg,
  "ru-RU": GooglePlayEnSvg,
  "zh-CN": GooglePlayEnSvg
} as const;

const IconWrapper = styled.span`
  min-width: 1.5rem;
  line-height: 1rem;
`;

const LanguageWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  flex-direction: row;
  gap: 0.5rem;
  padding: 0 0.5rem;
`;

const landingSupportedLanguages = supportedLanguages
  .filter((lang) => lang.code === "en-GB" || lang.code === "bg-BG" || lang.code === "ru-RU" || lang.code === "zh-CN")
  .map((l) => ({
    ...l,
    svgFlag: svgFlags[l.code]
  }));

export const Landing = () => {
  const {t, i18n} = useTranslation("landing");
  const offCanvasRef = useRef<Offcanvas | null>(null);
  const offCanvas: RefCallback<HTMLDivElement> = useCallback((element) => {
    offCanvasRef.current?.dispose();
    offCanvasRef.current = null;
    if (element) {
      offCanvasRef.current = new Offcanvas(element, {scroll: true});
    }
  }, []);
  useUnmount(() => {
    offCanvasRef.current?.dispose();
    offCanvasRef.current = null;
  });

  const closeMenu = useCallback(() => {
    offCanvasRef.current?.hide();
  }, []);

  const toggleOffCanvas = useCallback(() => {
    offCanvasRef.current?.toggle();
  }, []);

  const thisLanguage =
    landingSupportedLanguages.find((lang) => lang.code === i18n.language) ?? landingSupportedLanguages[0];

  const Badge = playBadges[thisLanguage.code];

  const device = useMemo(() => detectDevice(), []);

  const webLogin = `/employees?lang=${thisLanguage?.code}`;
  const employeeLoginHref = device === "android" ? GOOGLE_PLAY_URL : webLogin;
  const managerLoginHref = `/managers?lang=${thisLanguage?.code}`;

  const privacyPolicy = t("privacy-policy-url", {defaultValue: "/legal/privacy-policy/latest"});

  return (
    <>
      <div className="container-fluid nav-section sticky-top">
        <nav className="navbar navbar-expand-xl">
          <div className="container d-flex">
            <a className="navbar-brand d-inline-flex align-items-center gap-3" href="#">
              <Logo />
              <div className="d-flex align-items-center">
                <div className="d-inline-flex flex-column align-items-center gap-1">
                  <span className="brand-title lh-1">
                    <Trans i18nKey="title" t={t} />
                  </span>
                </div>
              </div>
            </a>
            <button className="navbar-toggler" onClick={toggleOffCanvas} type="button">
              <svg height={30} viewBox="0 0 30 30" width={30} xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4 7h22M4 15h22M4 23h22"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeMiterlimit="10"
                  strokeWidth="2"
                />
              </svg>
            </button>
            <div className="offcanvas offcanvas-end nav-section" ref={offCanvas}>
              <div className="offcanvas-header">
                <div className="offcanvas-title d-inline-flex align-items-center gap-4">
                  <Logo />
                  <div className="d-flex align-items-center">
                    <div className="d-inline-flex flex-column align-items-center gap-2">
                      <span className="brand-title lh-1">
                        <Trans i18nKey="title" t={t} />
                      </span>
                      <span className="fs-6 lh-1 d-none d-lg-inline">
                        <Trans i18nKey="subtitle" t={t} />
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  aria-label="Close"
                  className="btn-close btn-close-white"
                  data-bs-dismiss="offcanvas"
                  type="button"
                />
              </div>
              <div className="offcanvas-body justify-content-end">
                <ul className="navbar-nav p-2">
                  <li className="nav-item">
                    <a className="nav-link px-3" href="#начало" onClick={closeMenu}>
                      <Trans i18nKey="nav.home" t={t}>
                        Начало
                      </Trans>
                    </a>
                  </li>
                  <li className="nav-item separator" />
                  <li className="nav-item">
                    <a className="nav-link px-3" href="#решения" onClick={closeMenu}>
                      <Trans i18nKey="nav.solutions" t={t}>
                        Решения
                      </Trans>
                    </a>
                  </li>
                  <li className="nav-item separator" />
                  <li className="nav-item">
                    <a className="nav-link px-3" href="#услуги" onClick={closeMenu}>
                      <Trans i18nKey="nav.services" t={t}>
                        Услуги
                      </Trans>
                    </a>
                  </li>
                  <li className="nav-item separator" />
                  <li className="nav-item">
                    <a className="nav-link px-3" href="#контакти" onClick={closeMenu}>
                      <Trans i18nKey="nav.contacts" t={t}>
                        Контакти
                      </Trans>
                    </a>
                  </li>
                  <li className="nav-item separator" />
                  <li className="nav-item">
                    <a className="nav-link px-3" href="#за-нас" onClick={closeMenu}>
                      <Trans i18nKey="nav.about-us" t={t}>
                        За нас
                      </Trans>
                    </a>
                  </li>
                  {thisLanguage && (
                    <>
                      <li className="nav-item separator" />
                      <li className="nav-item">
                        <BootstrapDropdown
                          className="nav-link px-3"
                          value={
                            <LanguageWrapper>
                              <IconWrapper>
                                <LanguageSvg />
                              </IconWrapper>
                              <IconWrapper>{thisLanguage.svgFlag}</IconWrapper>
                            </LanguageWrapper>
                          }
                        >
                          {landingSupportedLanguages.map((l) => (
                            <a className="dropdown-item" href={`?lang=${l.code}`} key={l.code}>
                              <LanguageWrapper>
                                <IconWrapper>{l.svgFlag}</IconWrapper> {l.name}
                              </LanguageWrapper>
                            </a>
                          ))}
                        </BootstrapDropdown>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </div>
      <div className="container-fluid gradient-section-row" id="начало">
        <div className="container py-5">
          <div className="row align-items-justify g-5 py-5 g-lg-1 py-sm-1">
            <div className="col-12 col-lg-6 d-flex flex-column text-center text-lg-start align-items-stretch align-items-lg-start align-self-center">
              <h1 className="section-title hero-title display-3 fw-bold lh-sm mb-3">
                <Trans i18nKey="sec1.title1" t={t}>
                  Дигиталната трансформация на хранителната безопасност
                </Trans>
              </h1>
              <h2 className="display-7 lh-1 mb-5">
                <Trans i18nKey="sec1.title2" t={t}>
                  Без хартия. Всичко в телефона.
                </Trans>
              </h2>
              <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                <a
                  className="btn btn-primary btn-lg px-4 me-md-2"
                  href={employeeLoginHref}
                  rel={device === "android" ? "noopener noreferrer" : undefined}
                  target={device === "android" ? "_blank" : undefined}
                  type="button"
                >
                  <Trans i18nKey="sec1.button-employee-login" t={t} />
                </a>
                <a className="btn btn-secondary btn-lg px-4" href={managerLoginHref} type="button">
                  <Trans i18nKey="sec1.button-manager-login" t={t} />
                </a>
              </div>
              <div className="gap-3 d-flex justify-content-center align-items-center mt-3 mt-lg-5">
                {device === "other" && (
                  <a
                    className="d-none d-xl-block p-2 bg-white"
                    href={GOOGLE_PLAY_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                    type="button"
                  >
                    <QRCode size={96} value={GOOGLE_PLAY_URL} />
                  </a>
                )}

                {device !== "ios" && (
                  <a href={GOOGLE_PLAY_URL} rel="noopener noreferrer" target="_blank" type="button">
                    <Badge style={{height: "40px"}} />
                  </a>
                )}
                {device !== "android" && <OpenInSafariBadge href={webLogin} />}
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <ResponsiveImage
                alt=""
                className="drop-shadow"
                src={image1}
                style={{width: "100%", height: "100%", minHeight: "300px", aspectRatio: "800/893"}}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid secondary-section-row" id="решения">
        <div className="container py-sm-5 py-2">
          <div className="row row-cols-1 row-cols-lg-2 secondary-section rounded">
            <div className="col p-sm-5 p-4 ">
              <h2 className="section-title fs-1 pb-4 fw-bold">
                <Trans i18nKey="sec2.left.title" t={t}>
                  Нашите решения
                </Trans>
              </h2>
              <div className="fs-5">
                <ul>
                  <li>
                    <Trans i18nKey="sec2.left.item1" t={t}>
                      Дигитална платформа за входящ контрол и контролни карти
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.left.item2" t={t}>
                      Онлайн обучения на служителите по безопасност на храните
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.left.item3" t={t}>
                      Електронен архив на документи
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.left.item4" t={t}>
                      Консултации при отваряне на обект
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.left.item5" t={t}>
                      HACCP документация
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.left.item6" t={t}>
                      Вътрешни проверки и одити
                    </Trans>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col p-sm-5 p-4 primary-section fs-5 rounded">
              <h2 className="section-title fs-1 pb-4 fw-bold">
                <Trans i18nKey="sec2.right.title" t={t}>
                  Предимства
                </Trans>
              </h2>
              <div className="fs-5">
                <ul>
                  <li>
                    <Trans i18nKey="sec2.right.item1" t={t}>
                      Без хартия – всичко в телефона
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.right.item2" t={t}>
                      Видео уроци и тестове, организирани по позиции и нива – лесно и ефективно
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.right.item3" t={t}>
                      Автоматични напомняния за изтичащи срокове
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.right.item4" t={t}>
                      Спестяване на време и ресурси
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.right.item5" t={t}>
                      Лесна интеграция в ежедневната работа
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec2.right.item6" t={t}>
                      Създадена от консултанти с дългогодишен опит по безопасност на храните
                    </Trans>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid gradient-section-row" id="услуги">
        <div className="container px-4 py-5">
          <div className="row g-4">
            <h2 className="section-title fs-1 fw-bold">
              <Trans i18nKey="sec3.title" t={t}>
                Услуги
              </Trans>
            </h2>
          </div>
          <div className="row g-4 py-5 row-cols-1 row-cols-lg-2">
            <div className="primary-section col p-sm-5 p-4 rounded">
              <h3 className="fs-2 fw-bold">
                <Trans i18nKey="sec3.service1.title" t={t}>
                  Електронни контролни карти
                </Trans>
              </h3>
              <h4 className="fs-5 mb-4">
                <Trans i18nKey="sec3.service1.subtitle" t={t}>
                  Улеснено попълване на контролни карти директно от смартфон
                </Trans>
              </h4>
              <div className="fs-5">
                <ul>
                  <li>
                    <Trans i18nKey="sec3.service1.item1" t={t}>
                      Удобен интерфейс, който драстично намалява времето за попълване
                    </Trans>
                  </li>
                  <li>
                    <strong>
                      <Trans i18nKey="sec3.service1.item2" t={t}>
                        AI – Monitoring на контролни карти
                      </Trans>
                    </strong>
                    <ul>
                      <li>
                        <Trans i18nKey="sec3.service1.item3" t={t}>
                          Засичане на грешки, липсващи стойности и нереалистични данни
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec3.service1.item4" t={t}>
                          Интелигентен анализ на данните за повишаване на безопасността
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec3.service1.item5" t={t}>
                          Отчети и препоръки за подобрение
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec3.service1.item6" t={t}>
                          Повишена прозрачност и проследимост
                        </Trans>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <Trans i18nKey="sec3.service1.item7" t={t}>
                      Задаване на крайни срокове за попълване
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec3.service1.item8" t={t}>
                      Бърз достъп до попълнени контролни карти при проверка
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec3.service1.item9" t={t}>
                      Надежден архив на попълнените контролни карти
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec3.service1.item10" t={t}>
                      Административен панел за централизиран контрол на попълването
                    </Trans>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col d-flex justify-content-center">
              <ResponsiveImage
                alt=""
                className="drop-shadow"
                src={image2}
                style={{width: "100%", height: "100%", aspectRatio: "616/1256", maxHeight: 600, minHeight: 300}}
              />
            </div>
          </div>
          <div className="row g-4 py-5 row-cols-1 row-cols-lg-2 flex-lg-row-reverse">
            <div className="col primary-section p-sm-5 p-4 rounded">
              <h3 className="fs-2 fw-bold">
                <Trans i18nKey="sec5.service2.title" t={t}>
                  Входящ контрол
                </Trans>
              </h3>
              <h4 className="fs-5 mb-4">
                <Trans i18nKey="sec5.service2.subtitle" t={t}>
                  Спестява време и усилия за ръчна обработка на придружаващите документи
                </Trans>
              </h4>
              <div className="fs-5">
                <ul>
                  <li>
                    <Trans i18nKey="sec5.service2.item1" t={t}>
                      Директно добавяне на документите чрез камерата на телефона в защитено облачно пространство, без да
                      натоварват паметта му
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.service2.item2" t={t}>
                      Възможност за работа на няколко служителя едновременно – служител, деловодител, счетоводител,
                      контролен орган
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.service2.item3" t={t}>
                      Преглед, споделяне или разпечатване на вече създадените документи
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.service2.item4" t={t}>
                      Търсене и филтриране на документите по зададени критерии
                    </Trans>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col d-flex justify-content-center">
              <ResponsiveImage
                alt=""
                className="drop-shadow"
                src={image3}
                style={{width: "100%", height: "100%", aspectRatio: "616/1256", maxHeight: 600, minHeight: 300}}
              />
            </div>
          </div>
          <div className="row g-4 py-5 row-cols-1 row-cols-lg-2">
            <div className="primary-section col p-sm-5 p-4 rounded">
              <h3 className="fs-2 fw-bold">
                <Trans i18nKey="sec5.training.subtitle1" t={t}>
                  Обучение по безопасност на храните
                </Trans>
              </h3>
              <h4 className="fs-5 mb-4">
                <Trans i18nKey="sec5.training.subtitle" t={t}>
                  Заменя лекционната форма с модерно и привличащо вниманието мултимедийно обучение
                </Trans>
              </h4>
              <div className="fs-5">
                <ul>
                  <li>
                    <Trans i18nKey="sec5.training.item1" t={t}>
                      Систематизирано и актуално съдържание – над 130 страници материал в 10 видео урока по около 20
                      минути
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.training.item2" t={t}>
                      Отговарящо на световни високи изисквания за качество и ефективност на преподаване
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.training.item3" t={t}>
                      Тестова форма на проверка на знанията
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.training.item4" t={t}>
                      Лесно организиране на обученията спрямо нуждите и заеманите позиции на служителите
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.training.item5" t={t}>
                      Възможност за планиране на опреснителни обучения през календара на платформата
                    </Trans>
                  </li>
                  <li>
                    <Trans
                      components={{
                        Cpd: (
                          <a
                            href="https://www.cpduk.co.uk/providers/ef-es-academy"
                            rel="noopener noreferrer"
                            target="_blank"
                          />
                        )
                      }}
                      i18nKey="sec5.training.item7"
                      t={t}
                    />
                  </li>
                </ul>
                <ResponsiveImage
                  alt=""
                  className="drop-shadow"
                  src={cpd}
                  style={{width: "100%", height: "100%", aspectRatio: "300/236", maxHeight: 150, minHeight: 100}}
                />
              </div>
            </div>
            <div className="col d-flex justify-content-center align-items-center flex-column gap-4">
              <ResponsiveImage
                alt=""
                className="drop-shadow"
                src={image4}
                style={{width: "100%", height: "100%", aspectRatio: "616/1256", maxHeight: 600, minHeight: 300}}
              />
            </div>
          </div>

          <div className="row g-4 py-5 row-cols-1 row-cols-lg-2 flex-lg-row-reverse">
            <div className="col primary-section p-sm-5 p-4 rounded">
              <h3 className="fs-2 fw-bold">
                <Trans i18nKey="sec5.subtitle4" t={t}>
                  Органайзер
                </Trans>
              </h3>
              <h4 className="fs-5 mb-4">
                <Trans i18nKey="sec5.organizer.subtitle" t={t}>
                  Дигитален архив на всички важни за организацията документи по институции
                </Trans>
              </h4>

              <div className="fs-5">
                <ul>
                  <li>
                    <Trans i18nKey="sec5.organizer.item1" t={t}>
                      Контролиран достъп на служителите до документите
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.organizer.item2" t={t}>
                      Възможност за добавяне на напомняния за обновяване на изтичащи документи със срок
                    </Trans>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.organizer.item3" t={t}>
                      Надеждно съхраняване на документите
                    </Trans>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col d-flex justify-content-center">
              <ResponsiveImage
                alt=""
                className="drop-shadow"
                src={image5}
                style={{width: "100%", height: "100%", aspectRatio: "616/1256", maxHeight: 600, minHeight: 300}}
              />
            </div>
          </div>

          <div className="row g-4 py-5 row-cols-1 row-cols-lg-2">
            <div className="primary-section col p-sm-5 p-4 rounded">
              <h3 className="fs-2 fw-bold">
                <Trans i18nKey="sec5.subtitle1" t={t}>
                  Консултантски услуги
                </Trans>
              </h3>
              <h4 className="fs-5 mb-4">
                <Trans i18nKey="sec5.consulting.subtitle" t={t}>
                  За безпроблемна работа на хранителен обект
                </Trans>
              </h4>
              <div className="fs-5">
                <ul>
                  <li>
                    <Trans i18nKey="sec5.consulting.group1.title" t={t}>
                      Вътрешни проверки
                    </Trans>
                    <ul>
                      <li>
                        <Trans i18nKey="sec5.consulting.group1.item1" t={t}>
                          Обективна оценка и реална представа за състоянието на обекта
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group1.item2" t={t}>
                          Проверки на място по утвърдени чеклисти
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group1.item3" t={t}>
                          Снимков материал и конкретни препоръки
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group1.item4" t={t}>
                          Съпоставка между реалното състояние и записите в системата
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group1.item5" t={t}>
                          Доклад с анализ и препоръки към собственика / управителя
                        </Trans>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.consulting.group2.title" t={t}>
                      Консултации при отваряне на хранителен обект
                    </Trans>
                    <ul>
                      <li>
                        <Trans i18nKey="sec5.consulting.group2.item1" t={t}>
                          Професионална помощ от първия ден
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group2.item2" t={t}>
                          Подготовка за първоначална регистрация
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group2.item3" t={t}>
                          Проверка на технологични проекти и поточност
                        </Trans>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <Trans i18nKey="sec5.consulting.group3.title" t={t}>
                      Документация и HACCP
                    </Trans>
                    <ul>
                      <li>
                        <Trans i18nKey="sec5.consulting.group3.item1" t={t}>
                          Пълна система от документи, готова за практическо прилагане
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group3.item2" t={t}>
                          Разработване и внедряване на HACCP план
                        </Trans>
                      </li>
                      <li>
                        <Trans i18nKey="sec5.consulting.group3.item3" t={t}>
                          Актуализация на документация при промени
                        </Trans>
                      </li>
                    </ul>
                  </li>
                </ul>
                <p className="mb-0">
                  <Trans
                    components={{Link: <a href="https://fsconsulting.bg/" rel="noopener noreferrer" target="_blank" />}}
                    i18nKey="sec5.consulting.link"
                    t={t}
                  />
                </p>
              </div>
            </div>
            <div className="col d-flex justify-content-center align-items-center">
              <ResponsiveImage
                alt=""
                className="rounded"
                src={image6}
                style={{width: "100%", height: "100%", aspectRatio: "727/1024", maxHeight: 600, minHeight: 300}}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid secondary-section-row" id="контакти">
        <div className="container px-4 pt-5">
          <div className="row row-cols-1 row-cols-lg-2 secondary-section rounded">
            <div className="col p-sm-5 p-4 ">
              <h2 className="section-title fs-1">
                <Trans i18nKey="sec7.title" t={t}>
                  За всякакви въпроси относно този продукт
                </Trans>
              </h2>
              <p className="fs-5">
                <Trans i18nKey="sec7.demo" t={t}>
                  За 10-минутно демо
                </Trans>
              </p>
            </div>
            <div className="col p-5 primary-section rounded">
              <p className="fs-5">
                <Trans
                  components={{Phone: <a href="tel:+359895775512">+359 895 77 55 12</a>}}
                  i18nKey="sec7.phone"
                  t={t}
                />
              </p>
              <p className="fs-5">
                <Trans
                  components={{Email: <a href="mailto:office@fsacademy.bg">office@fsacademy.bg</a>}}
                  i18nKey="sec7.email"
                  t={t}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid secondary-section-row" id="кариери">
        <div className="container px-4 py-5">
          <div className="row row-cols-1 row-cols-lg-2 secondary-section rounded">
            <div className="col p-sm-5 p-4 ">
              <h2 className="section-title pb-2 fs-3">
                <Trans i18nKey="sec8.title" t={t}>
                  Кариери
                </Trans>
              </h2>
              <h3 className="pb-2 fs-4">
                <Trans i18nKey="sec8.subtitle" t={t}>
                  Станeте част от екипа
                </Trans>
              </h3>
            </div>
            <div className="col p-5 primary-section fs-5 rounded">
              <Trans
                components={{Email: <a href="mailto:office@fsacademy.bg">office@fsacademy.bg</a>}}
                i18nKey="sec8.text"
                t={t}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid gradient-section-row" id="за-нас">
        <div className="container py-5">
          <div className="row row-cols-1 row-cols-lg-2">
            <div className="col pb-4">
              <h2 className="section-title fs-1 fw-bold">
                <Trans i18nKey="sec6.title" t={t}>
                  За нас
                </Trans>
              </h2>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <p className="fs-5">
                <Trans i18nKey="sec6.p1" t={t}>
                  Food Safety Academy е създадена с една ясна мисия – да направи безопасността на храните реална
                  практика, а не просто документ. Ние вярваме, че качествената и безопасна храна е резултат от знания,
                  прилагани ежедневно – не само по време на проверки, а във всеки работен ден, от всеки член на екипа.
                </Trans>
              </p>
              <p className="fs-5">
                <Trans i18nKey="sec6.p2" t={t}>
                  Нашите продукти и обучения са разработени така, че да обединят теорията с реалния опит от
                  професионалната кухня и контролната практика. Те дават на хората увереност да работят правилно, а на
                  бизнеса – спокойствие и проследимост.
                </Trans>
              </p>
              <p className="fs-5">
                <Trans i18nKey="sec6.p3" t={t}>
                  Знаем, че в среда, където често се правят компромиси, истинският професионализъм започва с лична
                  отговорност. Затова нашият екип създава решения, които подпомагат фирмите да изграждат култура на
                  безопасност – базирана на знание, прозрачност и реални действия.
                </Trans>
              </p>
              <p className="fs-5">
                <Trans i18nKey="sec6.p4" t={t}>
                  Food Safety Academy – защото има значение.
                </Trans>
              </p>
              <YoutubeLink alt={t("sec6.video.alt")} className="mt-4" poster={naKafeVideo} videoId={NA_KAFE_VIDEO_ID} />
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid px-2 py-4 fs-6 text-center">
        <Trans
          components={{PrivacyPolicy: <a href={privacyPolicy} rel="noreferrer" target="_blank" />}}
          i18nKey="footer.copyright"
          t={t}
        />
      </div>
    </>
  );
};

export default Landing;
